import { databases } from "@/lib/appwrite";
import { Query } from "appwrite";

const databaseId = import.meta.env.VITE_APPWRITE_DATABASE_ID!;
const phrasesCollectionId = import.meta.env.VITE_PHRASES_COLLECTION_ID!;

export interface MotivationalPhrase {
  $id: string;
  phrase: string;
  author: string;
  category: string;
  $createdAt: string;
  $updatedAt: string;
}

// Función para obtener todas las frases (para calcular el índice)
export async function getAllPhrasesCount(): Promise<number> {
  try {
    const response = await databases.listDocuments(
      databaseId,
      phrasesCollectionId,
      [
        Query.limit(1),
        Query.select(['$id'])
      ]
    );

    return response.total; // Appwrite devuelve el total sin importar el limit
  } catch (error) {
    console.log('Error getting phrases count:', error);
    return 0;
  }
}


export async function getTodaysPhrase(): Promise<MotivationalPhrase | null> {
  try {
    const countResponse = await databases.listDocuments(
      databaseId,
      phrasesCollectionId,
      [
        Query.limit(1),
        Query.select(['$id'])
      ]
    );

    const totalPhrases = countResponse.total;

    if (totalPhrases === 0) {
      return null;
    }

    // Calcular el índice basado en la fecha
    const today = new Date();
    const epochDays = Math.floor(today.getTime() / (1000 * 60 * 60 * 24));
    const phraseIndex = epochDays % totalPhrases;

    // Obtener solo la frase específica
    const response = await databases.listDocuments(
      databaseId,
      phrasesCollectionId,
      [
        Query.orderAsc('$createdAt'),
        Query.limit(1),
        Query.offset(phraseIndex)
      ]
    );

    return response.documents.length > 0 ? response.documents[0] as unknown as MotivationalPhrase : null;
  } catch (error) {
    console.log('Error getting today\'s phrase:', error);
    return null;
  }
}

// Función alternativa usando el día del año (más simple)
export async function getTodaysPhraseByDayOfYear(): Promise<MotivationalPhrase | null> {
  try {
    const totalPhrases = await getAllPhrasesCount();

    if (totalPhrases === 0) {
      return null;
    }

    // Obtener el día del año (1-365/366)
    const today = new Date();
    const startOfYear = new Date(today.getFullYear(), 0, 1);
    const dayOfYear = Math.floor((today.getTime() - startOfYear.getTime()) / (1000 * 60 * 60 * 24)) + 1;

    // Calcular el índice
    const phraseIndex = (dayOfYear - 1) % totalPhrases;

    const response = await databases.listDocuments(
      databaseId,
      phrasesCollectionId,
      [
        Query.orderAsc('$createdAt'),
        Query.limit(1),
        Query.offset(phraseIndex)
      ]
    );

    return response.documents.length > 0 ? response.documents[0] as unknown as MotivationalPhrase : null;
  } catch (error) {
    console.log('Error getting phrase by day of year:', error);
    return null;
  }
}

// Función para obtener frases aleatorias (backup)
export async function getRandomPhrase(): Promise<MotivationalPhrase | null> {
  try {
    const totalPhrases = await getAllPhrasesCount();

    if (totalPhrases === 0) {
      return null;
    }

    const randomIndex = Math.floor(Math.random() * totalPhrases);

    const response = await databases.listDocuments(
      databaseId,
      phrasesCollectionId,
      [
        Query.orderAsc('$createdAt'),
        Query.limit(1),
        Query.offset(randomIndex)
      ]
    );

    return response.documents.length > 0 ? response.documents[0] as unknown as MotivationalPhrase : null;
  } catch (error) {
    console.log('Error getting random phrase:', error);
    return null;
  }
}

// Función para obtener frases por categoría
export async function getPhrasesByCategory(category: string): Promise<MotivationalPhrase[]> {
  try {
    const response = await databases.listDocuments(
      databaseId,
      phrasesCollectionId,
      [
        Query.equal('category', category),
        Query.orderAsc('$createdAt'),
        Query.limit(100) // Ajusta según necesidad
      ]
    );

    return response.documents as unknown as MotivationalPhrase[];
  } catch (error) {
    console.log('Error getting phrases by category:', error);
    return [];
  }
}

// Función para obtener todas las categorías únicas
export async function getAllCategories(): Promise<string[]> {
  try {
    const response = await databases.listDocuments(
      databaseId,
      phrasesCollectionId,
      [
        Query.limit(1000), // Ajusta según tu cantidad
        Query.select(['category'])
      ]
    );

    // Extraer categorías únicas
    const categories = [...new Set(response.documents.map(doc => doc.category))];
    return categories.sort();
  } catch (error) {
    console.log('Error getting categories:', error);
    return [];
  }
}